import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  reload as reloadUser,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { affiliateService } from '../services/affiliateService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, firstName, lastName, referralCode = null) => {
    let referralValidation = null;

    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Combine first and last name for displayName
    const displayName = `${firstName} ${lastName}`;

    // Update the user's display name
    if (displayName && result.user) {
      await updateProfile(result.user, {
        displayName: displayName
      });
    }

    // Validate referral code if provided — do this after creating the auth user
    if (referralCode && referralCode.trim() !== '') {
      // Ensure auth token is available for Firestore rules (sometimes token propagation lags)
      try {
        await result.user.getIdToken(true);
      } catch (tokenErr) {
        console.warn('Failed to refresh ID token before referral validation:', tokenErr);
      }
      try {
        referralValidation = await affiliateService.validateReferralCode(referralCode);
        if (!referralValidation.isValid) {
          // If invalid, delete the newly created auth user to avoid orphan accounts
          try {
            await result.user.delete();
          } catch (delErr) {
            console.error('Failed to delete user after invalid referral code:', delErr);
          }
          throw new Error(referralValidation.error);
        }
      } catch (err) {
        // If validateReferralCode threw due to rules/permissions, delete user and propagate a friendly error
        try {
          await result.user.delete();
        } catch (delErr) {
          console.error('Failed to delete user after referral validation error:', delErr);
        }
        console.error('Error validating referral code during signup:', err);
        throw new Error('Unable to validate referral code. Please try again.');
      }
    }

    // Create user document in Firestore
    if (result.user) {
      // Force token refresh to ensure auth state is propagated to Firestore rules
      try {
        await result.user.getIdToken(true);
        // Small delay to ensure token propagation to Firestore
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (tokenErr) {
        console.warn('Token refresh before Firestore write failed:', tokenErr);
      }

      const userRef = doc(db, 'users', result.user.uid);

      // Invite-only affiliate program (2026-10-30): new users are NOT
      // affiliates and get NO code. Affiliate access is granted later by
      // an admin (manage-affiliates function), which stamps
      // affiliateAccess + affiliateCode + isAffiliate on the user doc.
      const affiliateCode = null;

      const userData = {
        firstName,
        lastName,
        displayName,
        email,
        isPremium: false,
        isAffiliate: false,
        affiliateCode: affiliateCode,
        photoURL: null,
        profileCreatedAt: new Date(),
        // Initialize referral fields
        referralUsers: [],
        referredByCode: referralValidation ? referralCode.trim().toUpperCase() : null,
        masterUser: referralValidation ? referralValidation.referrer.id : null,
        // Initialize role system
        role: 'user',
        roleAssignedAt: new Date(),
        roleAssignedBy: null
      };

      console.log('User data to be written:', userData);
      console.log('User ID:', result.user.uid);
      console.log('Auth user email verified:', result.user.emailVerified);

      try {
        console.log('Attempting to write to Firestore...');
        await setDoc(userRef, userData);
        console.log('User document created successfully');
      } catch (firestoreError) {
        console.error('Firestore document creation failed:', firestoreError);
        console.error('Error code:', firestoreError.code);
        console.error('Error message:', firestoreError.message);
        console.error('User data:', userData);
        
        // Delete the auth user since Firestore creation failed
        try {
          await result.user.delete();
          console.log('Auth user deleted after Firestore failure');
        } catch (deleteErr) {
          console.error('Failed to delete auth user:', deleteErr);
        }
        
        throw new Error('Failed to create user profile. Please try again.');
      }

      // If there's a valid referral, update the referrer's document
      if (referralValidation && referralValidation.isValid) {
        try {
          const referrerRef = doc(db, 'users', referralValidation.referrer.id);
          
          // Use arrayUnion to add the new user ID to the referralUsers array
          // This is cleaner and doesn't require reading the document first
          await updateDoc(referrerRef, {
            referralUsers: arrayUnion(result.user.uid)
          });
          
          console.log('Successfully updated referrer document');

          // NOTE: the legacy affiliateReferrals mapping (referrer ->
          // referred users' own affiliate codes) is no longer written:
          // new users have no affiliate code under the invite-only
          // program. Attribution lives in referredByCode / masterUser on
          // the new user's doc and referralUsers on the referrer's doc,
          // which is what the earnings ledger and dashboards read.
          console.log('Successfully created referral relationship');
        } catch (error) {
          console.error('Error updating referrer document:', error);
          console.error('Referral error code:', error.code);
          console.error('Referral error message:', error.message);
          // Don't throw here - user is already created, just log the referral issue
        }
      }
    }
    
    // Send email verification after user document creation and referral handling
    try {
      await sendEmailVerification(result.user, {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: false
      });
      console.log('Verification email sent successfully');
    } catch (emailError) {
      console.error('Error sending verification email:', emailError);
      console.error('Email error code:', emailError.code);
      console.error('Email error message:', emailError.message);
      // Don't throw - user is created, just log the email issue
    }

    return result;
  };

  // Login function
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Reset password function
  const resetPassword = (email) => {
    const actionCodeSettings = {
      url: `${process.env.REACT_APP_NETLIFY_SITE_URL || window.location.origin}/login?mode=resetPassword`,
      handleCodeInApp: false
    };
    return sendPasswordResetEmail(auth, email, actionCodeSettings);
  };

  // Refresh user function to check email verification status
  const refreshUser = async () => {
    if (auth.currentUser) {
      await reloadUser(auth.currentUser);
      setCurrentUser({ ...auth.currentUser });
      return auth.currentUser;
    }
    return null;
  };

  // Delete account function
  const deleteAccount = async (password) => {
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }

    try {
      // Re-authenticate user with password to confirm deletion
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      // Delete user document from Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userDocRef);

      // Delete the user from Firebase Authentication
      await deleteUser(currentUser);

      console.log('User account successfully deleted');
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Periodically check if email has been verified
  useEffect(() => {
    let interval;
    
    if (currentUser && !currentUser.emailVerified) {
      console.log('Starting email verification polling for user:', currentUser.email);
      // Check every 5 seconds if user's email has been verified
      interval = setInterval(async () => {
        try {
          if (auth.currentUser) {
            await reloadUser(auth.currentUser);
            console.log('Checked email verification status:', auth.currentUser.emailVerified);
            if (auth.currentUser.emailVerified) {
              console.log('Email verified! Updating user state...');
              setCurrentUser({ ...auth.currentUser });
              // Clear the dismissed flag when email is verified
              sessionStorage.removeItem('emailVerificationDismissed');
              localStorage.removeItem('emailVerificationDismissed');
              // Clear the interval since verification is complete
              if (interval) clearInterval(interval);
            }
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
        }
      }, 5000); // Check every 5 seconds
    } else if (currentUser && currentUser.emailVerified) {
      console.log('User email already verified, no polling needed');
    }

    return () => {
      if (interval) {
        console.log('Clearing email verification polling interval');
        clearInterval(interval);
      }
    };
  }, [currentUser]);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    refreshUser,
    deleteAccount
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
